import React, { useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import GradientDivider from 'components/lib/GradientDivider';
import Input from 'components/lib/Input';
import { useDebounce } from 'utils/hooks/useDebounce';

import { useShareViaEmailContext } from './ShareViaEmailContext';
import ShareViaEmailUsers from './ShareViaEmailUsers';
import { User } from './types';

const Label = styled.h1`
  font-size: 14px;
  color: #222;
  margin-top: 27px;
  margin-bottom: 5px;
`;

const UserContainer = styled.div<{ maxHeight?: string }>`
  max-height: ${({ maxHeight = '165px' }) => maxHeight};
  overflow-y: auto;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 15px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 900;
  display: flex;

  .ant-checkbox-label {
    display: flex;
  }

  .ant-checkbox-input {
    width: 18px;
    height: 18px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--positive-green);
    border-color: var(--positive-green);
  }
`;

type Props = {
  users: User[];
  selectedUsers: User[];
  onSelect: (user: User) => void;
  onDeselect: (user: User) => void;
  onSelectAll: () => void;
  onSearch: (value: string) => void;
  maxHeight?: string;
};

const ShareViaEmailExistingUsers: React.FC<Props> = ({
  users,
  selectedUsers,
  maxHeight,
  onSelect,
  onDeselect,
  onSelectAll,
  onSearch,
}) => {
  const remainingUsers = users.filter((user) => !selectedUsers.some((u) => u.id === user.id));
  const allSelected = users.length > 0 && remainingUsers.length === 0;
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue);
  const { notificationType } = useShareViaEmailContext();

  // Reset search when notification type changes
  useEffect(() => {
    setSearchValue('');
  }, [notificationType]);

  useEffect(() => {
    onSearch(debouncedSearchValue);
  }, [debouncedSearchValue, onSearch]);

  return (
    <div>
      <Label>Existing Users</Label>
      <Input
        prefix={<SearchOutlined />}
        value={searchValue}
        placeholder="Search People to share to"
        onChange={(e: any) => setSearchValue(e.target.value)}
      />
      <StyledCheckbox checked={allSelected} onChange={onSelectAll}>
        Select Recipients
      </StyledCheckbox>

      <UserContainer className="mini-scroll" maxHeight={maxHeight}>
        {selectedUsers.map((user) => (
          <ShareViaEmailUsers key={`selected-${user.id}`} checked user={user} handleChange={() => onDeselect(user)} />
        ))}

        {remainingUsers.length > 0 && selectedUsers.length > 0 && <GradientDivider />}

        {remainingUsers.map((user) => (
          <ShareViaEmailUsers key={user.id} user={user} handleChange={() => onSelect(user)} />
        ))}
      </UserContainer>
    </div>
  );
};

export default ShareViaEmailExistingUsers;
